import { useApi } from './useApi';
import { useAppState } from './useAppState';
import { getHttpInstance, getApiConfig } from './useApi';
export function useVouchers() {
    const api = useApi();
    const state = useAppState();
    function syncFavouriteFlagsFromState() {
        if (!state.availableVouchers || !state.favouriteDiscounts)
            return;
        const ids = new Set(state.favouriteDiscounts.map((f) => f.id));
        state.availableVouchers.forEach((v) => {
            v.favourite = ids.has(v.id);
        });
    }
    // Change key for pollUntilChanged: only the set of favourite ids matters,
    // so volatile fields on the payload don't bail the poll out early.
    const favouritesChangeKey = (data) => {
        const ids = Array.isArray(data) ? data.map((f) => f.id).sort() : [];
        return JSON.stringify(ids);
    };
    function getAvailableVouchers(onSuccess = null) {
        if (state.availableVouchers && state.availableVouchers.length > 0) {
            if (onSuccess)
                onSuccess();
            return;
        }
        api.GET("/Customers/00000000-0000-0000-0000-000000000000/AvailableDiscounts", (result) => {
            api.GET("/Customers/00000000-0000-0000-0000-000000000000/Discounts/Favourites", (f) => {
                state.favouriteDiscounts = f;
                state.availableVouchers = Array.isArray(result) ? result : [];
                state.availableVouchers.forEach((v) => {
                    v.favourite = state.favouriteDiscounts.some((fav) => fav.id == v.id);
                });
                if (onSuccess)
                    onSuccess();
            }, true);
        }, true);
    }
    function getCustomerFavouriteVouchers(onSuccess = null, refreshCache = false, staleWhileRevalidate = false, pollUntilChanged = false) {
        api.GET("/Customers/00000000-0000-0000-0000-000000000000/Discounts/Favourites", (result, isStale) => {
            state.favouriteDiscounts = Array.isArray(result) ? result : [];
            syncFavouriteFlagsFromState();
            if (onSuccess)
                onSuccess(result, isStale);
        }, !refreshCache, refreshCache, /* ttlMins */ undefined, /* retrying */ false, staleWhileRevalidate, pollUntilChanged, favouritesChangeKey);
    }
    function clearCustomerFavouriteVouchers() {
        const config = getApiConfig();
        localStorage.removeItem(config.api + (config.useCachedEndpoints ? '/Cached' : '') + '/Customers/00000000-0000-0000-0000-000000000000/Discounts/Favourites');
    }
    function addCustomerFavouriteDiscount(discount, onSuccess = null) {
        getHttpInstance().post(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/Discounts/Favourites/", discount)
            .then(() => {
            if (onSuccess)
                onSuccess();
            getCustomerFavouriteVouchers(null, /* refreshCache */ false, /* staleWhileRevalidate */ true, /* pollUntilChanged */ true);
        });
    }
    function removeCustomerFavouriteDiscount(discount, onSuccess = null) {
        getHttpInstance().delete(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/Discounts/Favourites/" + discount.id)
            .then(() => {
            if (onSuccess)
                onSuccess();
            getCustomerFavouriteVouchers(null, /* refreshCache */ false, /* staleWhileRevalidate */ true, /* pollUntilChanged */ true);
        });
    }
    function searchAvailableVouchers(search, onSuccess = null) {
        api.GET("/Customers/00000000-0000-0000-0000-000000000000/AvailableDiscounts?search=" + search, (result) => {
            if (onSuccess)
                onSuccess(result);
        }, true);
    }
    function getVoucherRecommendations(daysBack = -90, onSuccess = null) {
        api.GET("/Customers/00000000-0000-0000-0000-000000000000/Discounts?daysBack=" + daysBack, (result) => {
            if (onSuccess)
                onSuccess(result);
        }, true);
    }
    function getDiscountsStatement(onSuccess = null, useCache = false, forceSetCache = false) {
        api.GET("/Customers/00000000-0000-0000-0000-000000000000/Discounts/Statement", (result) => {
            if (onSuccess)
                onSuccess(result);
        }, useCache, forceSetCache);
    }
    function createCart(cartData) {
        sessionStorage.setItem('voucherCart', JSON.stringify(cartData));
    }
    function getCart(onSuccess = null) {
        const cart = sessionStorage.getItem('voucherCart');
        if (cart) {
            if (onSuccess)
                onSuccess(JSON.parse(cart));
        }
        else {
            if (onSuccess)
                onSuccess({ items: [] });
        }
    }
    function deleteCart() {
        sessionStorage.removeItem('voucherCart');
    }
    function getRecommendedVouchers(daysBack = -90, onSuccess = null) {
        getAvailableVouchers(() => {
            getVoucherRecommendations(daysBack, (r) => {
                const recommended = Array.isArray(r) ? r : [];
                let recommendedVouchers = state.availableVouchers.filter((v) => {
                    return recommended.some((rec) => rec.merchantId == v.merchant.sysoMerchantId);
                });
                recommendedVouchers.forEach((v) => {
                    const match = recommended.filter((rec) => rec.merchantId == v.merchant.sysoMerchantId)[0];
                    v.spent = match.amount;
                    v.saving = match.savingRounded;
                    v.biggestSaving = match.biggestSpend;
                    v.lastMonthSpend = match.lastMonthSpend;
                    v.transactionCount = match.transactionCount;
                    v.discountCategory = match.discountCategory;
                });
                recommendedVouchers.sort((a, b) => b.saving - a.saving);
                if (onSuccess)
                    onSuccess(recommendedVouchers);
            });
        });
    }
    // Stamps `availableVoucher` onto each transaction in `groups` for any
    // matching voucher in `state.availableVouchers`. Matches by the well-known
    // merchant id → sysoMerchantId first — `merchant.id` (MoneyPage shape) or
    // `merchantID` (spending-analysis shape) — then falls back to merchantName.
    function attachAvailableVouchers(groups) {
        const vouchers = state.availableVouchers ?? [];
        if (!groups || vouchers.length === 0)
            return;
        const byId = {};
        const byName = {};
        for (const v of vouchers) {
            const id = v.merchant?.sysoMerchantId;
            if (id && !byId[id])
                byId[id] = v;
            const name = v.merchant?.name;
            if (name && !byName[name.toLowerCase()])
                byName[name.toLowerCase()] = v;
        }
        for (const g of groups) {
            for (const t of g) {
                const sysoId = t.merchant?.id || t.merchantID;
                const v = (sysoId && byId[sysoId])
                    || byName[(t.merchantName ?? '').toLowerCase()];
                if (v)
                    t.availableVoucher = v;
            }
        }
    }
    function getAvailableVoucher(id, onSuccess = null) {
        api.GET("/Customers/00000000-0000-0000-0000-000000000000/AvailableDiscounts/" + id, (result) => {
            state.availableVoucher = result;
            if (onSuccess)
                onSuccess(result);
        }, true);
    }
    function getCategories(onSuccess = null) {
        api.GET("/Discounts/Categories", (result) => {
            state.categories = result;
            if (onSuccess)
                onSuccess();
        }, true);
    }
    function getCustomerVouchersGroupedByMerchant(onSuccess = null, archived = null, useCache = false, forceSetCache = false) {
        let url = "/Customers/00000000-0000-0000-0000-000000000000/Vouchers/MerchantGroups";
        if (archived !== null)
            url += "?archived=" + archived;
        api.GET(url, (result) => {
            state.groupedVouchers = result;
            if (onSuccess)
                onSuccess();
        }, useCache, forceSetCache);
    }
    function getCustomerVouchersSummary(onSuccess = null, useCache = false, forceSetCache = false) {
        api.GET("/Customers/00000000-0000-0000-0000-000000000000/Vouchers/Summary", (result) => {
            state.vouchersSummary = result;
            if (onSuccess)
                onSuccess();
        }, useCache, forceSetCache);
    }
    function getCustomerVouchers(onSuccess = null, merchantId = null, archived = null, useCache = false, forceSetCache = false) {
        let url = "/Customers/00000000-0000-0000-0000-000000000000/Vouchers";
        if (merchantId)
            url += "?merchantId=" + merchantId;
        if (archived !== null)
            url += (merchantId ? "&" : "?") + "archived=" + archived;
        api.GET(url, (result) => {
            state.vouchers = result;
            if (onSuccess)
                onSuccess();
        }, useCache, forceSetCache);
    }
    function getCustomerVoucher(id, onSuccess = null) {
        api.GET("/Customers/00000000-0000-0000-0000-000000000000/Vouchers/" + id, (result) => {
            state.voucher = result;
            if (onSuccess)
                onSuccess();
        });
    }
    function clearCustomerVouchers() {
        Object.keys(localStorage).forEach(key => {
            if (key.includes('/Customers/00000000-0000-0000-0000-000000000000/Vouchers')) {
                localStorage.removeItem(key);
            }
        });
    }
    function getPayment(paymentId, onSuccess = null) {
        api.GET("/Customers/00000000-0000-0000-0000-000000000000/Payments?providerPaymentId=" + paymentId, (result) => {
            state.payment = result;
            if (onSuccess)
                onSuccess();
        });
    }
    function createPayment(paymentRequest, onSuccess = null) {
        getHttpInstance().post(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/Payments", paymentRequest)
            .then((response) => { if (onSuccess)
            onSuccess(response); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => createPayment(paymentRequest, onSuccess));
            }
        });
    }
    function orderVoucher(slug, amount, paymentId, onSuccess = null) {
        getHttpInstance().post(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/Vouchers?brandSlug=" + slug + "&amount=" + amount + "&paymentId=" + paymentId, null)
            .then((response) => { if (onSuccess)
            onSuccess(response); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => orderVoucher(slug, amount, paymentId, onSuccess));
            }
        });
    }
    function deleteVoucherTransaction(voucherId, transactionId, onSuccess = null) {
        getHttpInstance().delete(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/Vouchers/" + voucherId + "/Transactions/" + transactionId)
            .then((response) => { if (onSuccess)
            onSuccess(response); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => deleteVoucherTransaction(voucherId, transactionId, onSuccess));
            }
        });
    }
    function updateVoucherTransaction(voucherId, transactionId, amount, onSuccess = null) {
        getHttpInstance().put(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/Vouchers/" + voucherId + "/Transactions/" + transactionId, { amount })
            .then(() => { if (onSuccess)
            onSuccess(); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => updateVoucherTransaction(voucherId, transactionId, amount, onSuccess));
            }
        });
    }
    function addVoucherEaseOfUse(easeOfUseData, onSuccess = null) {
        getHttpInstance().put(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/Vouchers/" + easeOfUseData.id + "/Ease", easeOfUseData.score, {
            headers: { 'Content-Type': 'application/json', 'accept': 'text/plain' }
        })
            .then(() => { if (onSuccess)
            onSuccess(); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => addVoucherEaseOfUse(easeOfUseData, onSuccess));
            }
        });
    }
    function archiveVoucher(id, onSuccess = null) {
        getHttpInstance().post(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/Vouchers/Archived/" + id, {
            headers: { 'Content-Type': 'application/json', 'accept': 'text/plain' }
        })
            .then(() => { if (onSuccess)
            onSuccess(); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => archiveVoucher(id, onSuccess));
            }
        });
    }
    function unarchiveVoucher(id, onSuccess = null) {
        getHttpInstance().delete(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/Vouchers/Archived/" + id, {
            headers: { 'Content-Type': 'application/json', 'accept': 'text/plain' }
        })
            .then(() => { if (onSuccess)
            onSuccess(); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => unarchiveVoucher(id, onSuccess));
            }
        });
    }
    function addVoucherTransaction(id, amount, onSuccess = null) {
        getHttpInstance().post(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/Vouchers/" + id + "/Transactions", { amount })
            .then((response) => { if (onSuccess)
            onSuccess(response.data); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => addVoucherTransaction(id, amount, onSuccess));
            }
        });
    }
    function removeUnavailableDiscountProviders(voucher) {
        if (voucher.stackWith) {
            voucher.stackWith.forEach((r) => {
                useDiscountProvider(r, (use) => {
                    if (!use)
                        voucher.stackWith.pop(r);
                });
            });
        }
    }
    function useDiscountProvider(provider, onSuccess = null) {
        let existing = localStorage.getItem('LinkedDiscountProviders');
        if (existing == null) {
            getBlueLightCard(() => {
                existing = localStorage.getItem('LinkedDiscountProviders');
                if (onSuccess)
                    onSuccess(existing.indexOf(provider) != -1);
            });
        }
        else {
            if (onSuccess)
                onSuccess(existing.indexOf(provider) != -1);
        }
    }
    function getBlueLightCard(onSuccess = null) {
        let existing = JSON.parse(localStorage.getItem('LinkedDiscountProviders') || 'null');
        if (existing == null)
            existing = [];
        localStorage.setItem('LinkedDiscountProviders', JSON.stringify(existing));
        api.GET("/Customers/00000000-0000-0000-0000-000000000000/Documents/BlueLightCard", (result) => {
            if (result && result != "") {
                if (existing.indexOf("Blue Light Card") == -1) {
                    existing.push("Blue Light Card");
                    localStorage.setItem('LinkedDiscountProviders', JSON.stringify(existing));
                }
            }
            if (onSuccess)
                onSuccess(result);
        }, true);
    }
    return {
        getAvailableVouchers,
        attachAvailableVouchers,
        getCustomerFavouriteVouchers,
        clearCustomerFavouriteVouchers,
        addCustomerFavouriteDiscount,
        removeCustomerFavouriteDiscount,
        searchAvailableVouchers,
        getVoucherRecommendations,
        getDiscountsStatement,
        createCart,
        getCart,
        deleteCart,
        getRecommendedVouchers,
        getAvailableVoucher,
        getCategories,
        getCustomerVouchersGroupedByMerchant,
        getCustomerVouchersSummary,
        getCustomerVouchers,
        getCustomerVoucher,
        clearCustomerVouchers,
        getPayment,
        createPayment,
        orderVoucher,
        deleteVoucherTransaction,
        updateVoucherTransaction,
        addVoucherEaseOfUse,
        archiveVoucher,
        unarchiveVoucher,
        addVoucherTransaction,
        removeUnavailableDiscountProviders,
        useDiscountProvider,
        getBlueLightCard,
    };
}
